import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage, useTheme } from '../App';
import { authAPI } from '../lib/api';
import { describeApiError } from '../lib/apiErrors';
import { toast } from 'sonner';
import { AuthHero } from '../components/auth/AuthHero';
import { AuthTopBar } from '../components/auth/AuthTopBar';
import { GuestAccessCard } from '../components/auth/GuestAccessCard';
import { LoginRegisterForm } from '../components/auth/LoginRegisterForm';
import { BrandFooter } from '../components/BrandFooter';
import { PrivateSeo } from '../components/seo/PrivateSeo';

export default function AuthPage() {
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const { loginUser } = useAuth();
  const { language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const labels = language === 'fr'
    ? { retry: 'R\u00e9essayer', signIn: 'Se connecter', dismiss: 'Fermer' }
    : { retry: 'Retry', signIn: 'Sign in', dismiss: 'Close' };

  /** Localized error toast with a contextual action (retry / switch to login). */
  const showApiError = (err, retry) => {
    const { kind, message } = describeApiError(err, language);
    const options = { duration: 7000 };
    if (kind === 'conflict') {
      options.action = { label: labels.signIn, onClick: () => setIsLogin(true) };
    } else if ((kind === 'unavailable' || kind === 'network' || kind === 'timeout' || kind === 'server') && retry) {
      options.action = { label: labels.retry, onClick: retry };
    }
    toast.error(message, options);
  };

  const handleSubmit = async (params) => {
    const { isLogin: loginMode, email, password, name } = params;
    setLoading(true);
    try {
      const res = loginMode
        ? await authAPI.login({ email, password })
        : await authAPI.register({ email, password, name, language });
      loginUser(res.data.token, res.data.user);
      toast.success(loginMode
        ? (language === 'fr' ? 'Bienvenue !' : 'Welcome back!')
        : (language === 'fr' ? 'Compte cr\u00e9\u00e9 !' : 'Account created!'));
      navigate(res.data.user.onboarding_complete ? '/dashboard' : '/onboarding');
    } catch (err) {
      showApiError(err, () => handleSubmit(params));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setGuestLoading(true);
    try {
      const res = await authAPI.guest(language);
      loginUser(res.data.token, res.data.user);
      toast.success(language === 'fr' ? 'Bienvenue !' : 'Welcome!');
      navigate('/onboarding');
    } catch (err) {
      showApiError(err, handleGuestLogin);
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--lux-ink)]" data-testid="auth-page">
      <PrivateSeo titleKey="login" />
      <AuthHero language={language} />

      <div className="flex-1 flex flex-col px-6 py-8">
        <AuthTopBar language={language} setLanguage={setLanguage} theme={theme} toggleTheme={toggleTheme} />

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md fade-in">
            <GuestAccessCard language={language} loading={guestLoading} onGuestLogin={handleGuestLogin} />

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-white/30 uppercase tracking-wider">
                {language === 'fr' ? 'ou connectez-vous' : 'or sign in'}
              </span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <LoginRegisterForm language={language} loading={loading} onSubmit={handleSubmit} isLogin={isLogin} onModeChange={setIsLogin} />
          </div>
        </div>

        <BrandFooter variant="auth" />
      </div>
    </div>
  );
}
